import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { API_BASE, apiFetch } from "../api";

// Push to a closed app needs the Firebase files (see the setup guide). Until they
// are added, the app is built with REACT_APP_PUSH unset so nothing here runs: on
// Android, asking for a push token without them can crash the app.
const ENABLED = process.env.REACT_APP_PUSH === "1";
const TOKEN_KEY = "budgetsprint-push-token";

// Push to a closed app is Android-only for now: iPhone push needs a paid Apple developer
// account (APNs). iPhones still get live updates while the app is open, and local reminders.
const isNative = () => Capacitor.getPlatform() === "android";

const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const saveToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // ignore
  }
};

// Asks for permission, gets this phone's push address and gives it to the server.
// `onTap` runs when the person taps a push notification. Returns a stop function.
export const startPush = async ({ onTap }) => {
  if (!ENABLED || !isNative()) return () => {};

  const handles = [];
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return () => {};

    await PushNotifications.createChannel({ id: "task-reminders", name: "Task reminders", description: "Reminders for your tasks", importance: 5, visibility: 1, vibration: true });

    handles.push(
      await PushNotifications.addListener("registration", (result) => {
        saveToken(result.value);
        apiFetch(`${API_BASE}/devices`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: result.value, platform: Capacitor.getPlatform() }),
        }).catch(() => {});
      }),
      await PushNotifications.addListener("pushNotificationActionPerformed", () => onTap && onTap())
    );
    await PushNotifications.register();
  } catch {
    // Push is optional; the app keeps working through the live connection and local reminders.
  }
  return () => handles.forEach((h) => h.remove().catch(() => {}));
};

// Call before logging out, so this phone stops getting the previous person's alerts.
export const forgetDevice = async () => {
  const token = readToken();
  if (!token) return;
  try {
    await apiFetch(`${API_BASE}/devices`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    // ignore
  }
};
